import React from "react";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import ProctorChart from "./ProctorChart";
import { evalFormulasWithReport } from "./ExcelImporter";

// ─── Exact cell map for the blank Proctor template ────────────────────────────
// Each entry: { id, label, addr, section, type, computed? }
export const PROCTOR_FIELDS = [
  // General Info
  { id: "project",     label: "Project",                        addr: "B3",  section: "General Info",  type: "text" },
  { id: "county",      label: "County",                         addr: "E3",  section: "General Info",  type: "text" },
  { id: "permit",      label: "Permit",                         addr: "B4",  section: "General Info",  type: "text" },
  { id: "method",      label: "Proctor Method",                 addr: "E4",  section: "General Info",  type: "text", placeholder: "e.g. D1557" },
  { id: "location",    label: "Location",                       addr: "B5",  section: "General Info",  type: "text" },
  { id: "lab",         label: "Lab",                            addr: "E5",  section: "General Info",  type: "text" },
  { id: "material",    label: "Material / Source",              addr: "B6",  section: "General Info",  type: "text" },
  { id: "testedby",    label: "Tested By",                      addr: "E6",  section: "General Info",  type: "text" },
  { id: "testdate",    label: "Test Date",                      addr: "B7",  section: "General Info",  type: "date" },
  { id: "specitem",    label: "Spec / Item",                    addr: "E7",  section: "General Info",  type: "text" },

  // Oversize Correction
  { id: "ov_pct",      label: "Percent oversize P (%)",         addr: "O4",  section: "Oversize Correction", type: "number", placeholder: "0",    note: "Enter from gradation; sum % retained on 3/4 in. sieve" },
  { id: "ov_gm",       label: "Bulk specific gravity (Gm)",     addr: "O5",  section: "Oversize Correction", type: "number", placeholder: "2.65", note: "Typical crushed stone" },
  { id: "ov_yw",       label: "Unit weight of water γw (pcf)",  addr: "O6",  section: "Oversize Correction", type: "number", placeholder: "62.4", note: "Constant" },

  // Trial 1
  { id: "t1_moldwet",  label: "Mold + Wet (g)",    addr: "B10", section: "Compaction Data", type: "number", trial: 1 },
  { id: "t1_mold",     label: "Mold (g)",           addr: "C10", section: "Compaction Data", type: "number", trial: 1 },
  { id: "t1_moldvol",  label: "Mold Vol (ft³)",     addr: "E10", section: "Compaction Data", type: "select", options: "0.0033,0.075", trial: 1, placeholder: "0.0033" },
  { id: "t1_tarewet",  label: "Tare + Wet (g)",     addr: "G10", section: "Compaction Data", type: "number", trial: 1 },
  { id: "t1_taredry",  label: "Tare + Dry (g)",     addr: "H10", section: "Compaction Data", type: "number", trial: 1 },
  { id: "t1_tare",     label: "Tare Weight (g)",    addr: "I10", section: "Compaction Data", type: "number", trial: 1 },

  // Trial 2
  { id: "t2_moldwet",  label: "Mold + Wet (g)",    addr: "B11", section: "Compaction Data", type: "number", trial: 2 },
  { id: "t2_mold",     label: "Mold (g)",           addr: "C11", section: "Compaction Data", type: "number", trial: 2 },
  { id: "t2_moldvol",  label: "Mold Vol (ft³)",     addr: "E11", section: "Compaction Data", type: "select", options: "0.0033,0.075", trial: 2, placeholder: "0.0033" },
  { id: "t2_tarewet",  label: "Tare + Wet (g)",     addr: "G11", section: "Compaction Data", type: "number", trial: 2 },
  { id: "t2_taredry",  label: "Tare + Dry (g)",     addr: "H11", section: "Compaction Data", type: "number", trial: 2 },
  { id: "t2_tare",     label: "Tare Weight (g)",    addr: "I11", section: "Compaction Data", type: "number", trial: 2 },

  // Trial 3
  { id: "t3_moldwet",  label: "Mold + Wet (g)",    addr: "B12", section: "Compaction Data", type: "number", trial: 3 },
  { id: "t3_mold",     label: "Mold (g)",           addr: "C12", section: "Compaction Data", type: "number", trial: 3 },
  { id: "t3_moldvol",  label: "Mold Vol (ft³)",     addr: "E12", section: "Compaction Data", type: "select", options: "0.0033,0.075", trial: 3, placeholder: "0.0033" },
  { id: "t3_tarewet",  label: "Tare + Wet (g)",     addr: "G12", section: "Compaction Data", type: "number", trial: 3 },
  { id: "t3_taredry",  label: "Tare + Dry (g)",     addr: "H12", section: "Compaction Data", type: "number", trial: 3 },
  { id: "t3_tare",     label: "Tare Weight (g)",    addr: "I12", section: "Compaction Data", type: "number", trial: 3 },

  // Trial 4
  { id: "t4_moldwet",  label: "Mold + Wet (g)",    addr: "B13", section: "Compaction Data", type: "number", trial: 4 },
  { id: "t4_mold",     label: "Mold (g)",           addr: "C13", section: "Compaction Data", type: "number", trial: 4 },
  { id: "t4_moldvol",  label: "Mold Vol (ft³)",     addr: "E13", section: "Compaction Data", type: "select", options: "0.0033,0.075", trial: 4, placeholder: "0.0033" },
  { id: "t4_tarewet",  label: "Tare + Wet (g)",     addr: "G13", section: "Compaction Data", type: "number", trial: 4 },
  { id: "t4_taredry",  label: "Tare + Dry (g)",     addr: "H13", section: "Compaction Data", type: "number", trial: 4 },
  { id: "t4_tare",     label: "Tare Weight (g)",    addr: "I13", section: "Compaction Data", type: "number", trial: 4 },

];

// Build template object from field map
export function buildProctorTemplate(id) {
  const sections = ["General Info", "Oversize Correction", "Compaction Data"];
  return {
    id,
    name: "Proctor — D698/D1557",
    description: "Moisture–Density Relationship",
    isExcelImport: true,
    isProctor: true,
    sections: sections.map(secName => ({
      id: `${id}-${secName}`,
      name: secName,
      fields: PROCTOR_FIELDS
        .filter(f => f.section === secName)
        .map(f => ({
          id: f.id,
          label: f.label,
          type: f.type,
          options: f.options || "",
          defaultValue: f.placeholder || "",
          note: f.note || "",
          _addr: f.addr,
          _trial: f.trial,
        })),
    })),
  };
}

// Known cell addresses for each trial row (0-indexed: trial 1 = row index 0).
// Input cells (yellow): moldwet, mold, moldvol, tarewet, taredry, tare
// Formula cells (evaluated by Excel): wetsoil, wetdensity, moisture, dryDensity, ddCorr
const TRIAL_ADDR = [
  { moldwet:"B10", mold:"C10", wetsoil:"D10", moldvol:"E10", wetdensity:"F10", tarewet:"G10", taredry:"H10", tare:"I10", moisture:"J10", dryDensity:"K10", ddCorr:"L10" },
  { moldwet:"B11", mold:"C11", wetsoil:"D11", moldvol:"E11", wetdensity:"F11", tarewet:"G11", taredry:"H11", tare:"I11", moisture:"J11", dryDensity:"K11", ddCorr:"L11" },
  { moldwet:"B12", mold:"C12", wetsoil:"D12", moldvol:"E12", wetdensity:"F12", tarewet:"G12", taredry:"H12", tare:"I12", moisture:"J12", dryDensity:"K12", ddCorr:"L12" },
  { moldwet:"B13", mold:"C13", wetsoil:"D13", moldvol:"E13", wetdensity:"F13", tarewet:"G13", taredry:"H13", tare:"I13", moisture:"J13", dryDensity:"K13", ddCorr:"L13" },
];

// ─── Compute intermediate values from trial inputs ─────────────────────────────
// Accepts optional template — if present, looks up values by _addr so it works
// with both the old hardcoded field IDs and the generic Excel parser field IDs.
export function computeTrials(report, template) {
  // Build addr→value map from report using template field _addr if available
  const addrMap = {};
  if (template) {
    for (const sec of template.sections || []) {
      for (const field of sec.fields || []) {
        if (field._addr && report[field.id] !== undefined && report[field.id] !== "") {
          addrMap[field._addr.toUpperCase()] = report[field.id];
        }
      }
    }
  }
  const getVal = (hardcodedKey, t, addr) => {
    // Try hardcoded ID first (backwards compat), then addr lookup
    const byId = report[`t${t}_${hardcodedKey}`];
    if (byId !== undefined && byId !== "") return byId;
    return addrMap[addr.toUpperCase()];
  };

  const trials = [];
  for (let t = 1; t <= 4; t++) {
    const addrs = TRIAL_ADDR[t - 1];
    const moldwet = parseFloat(getVal("moldwet", t, addrs.moldwet));
    const mold    = parseFloat(getVal("mold",    t, addrs.mold));
    const moldvol = parseFloat(getVal("moldvol", t, addrs.moldvol)) || 0.0033;
    const tarewet = parseFloat(getVal("tarewet", t, addrs.tarewet));
    const taredry = parseFloat(getVal("taredry", t, addrs.taredry));
    const tare    = parseFloat(getVal("tare",    t, addrs.tare));

    if (isNaN(moldwet) || isNaN(mold)) { trials.push(null); continue; }

    const wetSoil    = moldwet - mold;
    const wetDensity = (wetSoil / 453.59237) / moldvol;
    const moisture   = ((tarewet - taredry) / (taredry - tare)) * 100;
    const dryDensity = wetDensity / (1 + moisture / 100);

    const P  = parseFloat(report.ov_pct) || parseFloat(addrMap["O4"]) || 0;
    const Gm = parseFloat(report.ov_gm)  || parseFloat(addrMap["O5"]) || 2.65;
    const yw = parseFloat(report.ov_yw)  || parseFloat(addrMap["O6"]) || 62.4;
    const ddCorr = (dryDensity * (100 - P)) / (100 - P * (dryDensity / (Gm * yw)));

    trials.push({ t: t + 1, moisture, wetDensity, dryDensity, ddCorr, wetSoil, moldvol });
  }
  return trials;
}

// Fit quadratic to moisture vs dryDensity points → return coefficients [a, b, c]
// y = a*x^2 + b*x + c
export function fitQuadratic(points) {
  // Filter valid points
  const pts = points.filter(p => p && !isNaN(p.x) && !isNaN(p.y));
  if (pts.length < 3) return null;
  // Least squares for y = a*x^2 + b*x + c
  const n = pts.length;
  let s0=n, s1=0, s2=0, s3=0, s4=0, t0=0, t1=0, t2=0;
  pts.forEach(({x,y}) => {
    s1+=x; s2+=x*x; s3+=x*x*x; s4+=x*x*x*x;
    t0+=y; t1+=x*y; t2+=x*x*y;
  });
  // Solve 3x3 system: [[s0,s1,s2],[s1,s2,s3],[s2,s3,s4]] * [c,b,a] = [t0,t1,t2]
  const A = [[s0,s1,s2],[s1,s2,s3],[s2,s3,s4]];
  const B = [t0,t1,t2];
  // Gaussian elimination
  for (let i=0;i<3;i++) {
    let maxRow=i;
    for (let k=i+1;k<3;k++) if (Math.abs(A[k][i])>Math.abs(A[maxRow][i])) maxRow=k;
    [A[i],A[maxRow]]=[A[maxRow],A[i]]; [B[i],B[maxRow]]=[B[maxRow],B[i]];
    if (Math.abs(A[i][i])<1e-12) return null;
    for (let k=i+1;k<3;k++) {
      const f=A[k][i]/A[i][i];
      for (let j=i;j<3;j++) A[k][j]-=f*A[i][j];
      B[k]-=f*B[i];
    }
  }
  const x = [0,0,0];
  for (let i=2;i>=0;i--) {
    x[i]=B[i]/A[i][i];
    for (let k=i-1;k>=0;k--) B[k]-=A[k][i]*x[i];
  }
  const [c,b,a] = x;
  if (isNaN(a)||isNaN(b)||isNaN(c)) return null;
  return {a,b,c};
}

// ─── xlsx namespace constant ───────────────────────────────────────────────────
const OOXML_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const OOXML_REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

// Convert column letter(s) to 1-based number (A→1, Z→26, AA→27, …)
function colToNum(col) {
  let n = 0;
  for (const ch of col) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

// Resolve the xl/sheets/sheetN.xml path for a named sheet via workbook.xml.rels
async function resolveSheetPath(zip, sheetName) {
  const wbXml = await zip.file("xl/workbook.xml").async("string");
  const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
  const sheets = wbDoc.getElementsByTagNameNS(OOXML_NS, "sheet");
  let rId = null;
  for (const s of sheets) {
    const name = s.getAttribute("name") || "";
    if (name === sheetName || /^proctor/i.test(name)) {
      rId = s.getAttributeNS(OOXML_REL_NS, "id") || s.getAttribute("r:id");
      break;
    }
  }
  if (!rId) return null;
  const relsXml = await zip.file("xl/_rels/workbook.xml.rels").async("string");
  const relsDoc = new DOMParser().parseFromString(relsXml, "application/xml");
  for (const rel of relsDoc.getElementsByTagName("Relationship")) {
    if (rel.getAttribute("Id") === rId) {
      const target = rel.getAttribute("Target");
      return target.startsWith("/") ? target.slice(1) : `xl/${target}`;
    }
  }
  return null;
}

// Patch specific cell values in a sheet XML string without touching anything else
function patchSheetCells(sheetXml, cellValues) {
  const doc = new DOMParser().parseFromString(sheetXml, "application/xml");
  if (doc.querySelector("parsererror")) return sheetXml; // bail on bad XML

  // Index existing cell elements by address
  const cellMap = {};
  for (const c of doc.getElementsByTagNameNS(OOXML_NS, "c")) {
    const r = c.getAttribute("r");
    if (r) cellMap[r] = c;
  }

  const sheetData = doc.getElementsByTagNameNS(OOXML_NS, "sheetData")[0];

  for (const [addr, rawVal] of Object.entries(cellValues)) {
    if (rawVal === "" || rawVal == null) continue;
    const num = parseFloat(rawVal);
    const isNum = !isNaN(num);

    let cell = cellMap[addr];
    if (!cell) {
      // Create the cell (and its row if needed)
      const m = addr.match(/^([A-Z]+)(\d+)$/);
      if (!m) continue;
      const rowN = parseInt(m[2]);
      const allRows = Array.from(doc.getElementsByTagNameNS(OOXML_NS, "row"));
      let row = allRows.find(r => parseInt(r.getAttribute("r")) === rowN);
      if (!row) {
        row = doc.createElementNS(OOXML_NS, "row");
        row.setAttribute("r", m[2]);
        const before = allRows.find(r => parseInt(r.getAttribute("r")) > rowN);
        if (before) sheetData.insertBefore(row, before); else sheetData.appendChild(row);
      }
      cell = doc.createElementNS(OOXML_NS, "c");
      cell.setAttribute("r", addr);
      const colN = colToNum(m[1]);
      const rowCells = Array.from(row.getElementsByTagNameNS(OOXML_NS, "c"));
      const beforeCell = rowCells.find(c => {
        const cm = (c.getAttribute("r") || "").match(/^([A-Z]+)/);
        return cm && colToNum(cm[1]) > colN;
      });
      if (beforeCell) row.insertBefore(cell, beforeCell); else row.appendChild(cell);
      cellMap[addr] = cell;
    }

    // Remove formula (f), value (v), and inline string (is) children
    for (const tag of ["f", "v", "is"]) {
      for (const el of Array.from(cell.getElementsByTagNameNS(OOXML_NS, tag))) {
        cell.removeChild(el);
      }
    }

    // Write new value
    if (isNum) {
      cell.removeAttribute("t");
      const v = doc.createElementNS(OOXML_NS, "v");
      v.textContent = String(num);
      cell.appendChild(v);
    } else {
      cell.setAttribute("t", "str");
      const v = doc.createElementNS(OOXML_NS, "v");
      v.textContent = String(rawVal);
      cell.appendChild(v);
    }
  }

  return new XMLSerializer().serializeToString(doc);
}

// ─── Excel Export — patches values directly into original xlsx bytes ───────────
// rawBytes is a Uint8Array of the original blank template file.
// This preserves the native chart, formulas, styles, and drawings.
export async function exportProctorToExcel(report, rawBytes) {
  if (!rawBytes) {
    alert("Original template bytes not available. Please re-import the blank template.");
    return;
  }
  try {
    const zip = await JSZip.loadAsync(rawBytes);

    const sheetPath = await resolveSheetPath(zip, "Proctor");
    if (!sheetPath) throw new Error("Could not locate Proctor sheet in workbook");

    // Build the map of addr → value from all filled fields
    const cellValues = {};
    PROCTOR_FIELDS.forEach(f => {
      const v = report[f.id];
      const effective = (v !== undefined && v !== null && v !== "") ? v : (f.placeholder || "");
      if (effective !== "") cellValues[f.addr] = effective;
    });
    if (report.reportTitle) cellValues["A1"] = report.reportTitle;

    const patched = patchSheetCells(await zip.file(sheetPath).async("string"), cellValues);
    zip.file(sheetPath, patched);

    // Remove stale calculation chain so Excel rebuilds it on open
    zip.remove("xl/calcChain.xml");

    // Force auto-calculation mode so formulas recalc on open without manual triggers
    const wbXml = await zip.file("xl/workbook.xml").async("string");
    const wbDoc = new DOMParser().parseFromString(wbXml, "application/xml");
    let calcPr = wbDoc.getElementsByTagNameNS(OOXML_NS, "calcPr")[0];
    if (!calcPr) {
      calcPr = wbDoc.createElementNS(OOXML_NS, "calcPr");
      wbDoc.getElementsByTagNameNS(OOXML_NS, "workbook")[0].appendChild(calcPr);
    }
    calcPr.setAttribute("calcMode", "auto");
    calcPr.setAttribute("fullCalcOnLoad", "1");
    zip.file("xl/workbook.xml", new XMLSerializer().serializeToString(wbDoc));

    // Detect xlsm (macro-enabled) by checking Content_Types
    const contentTypes = await zip.file("[Content_Types].xml").async("string");
    const isXlsm = contentTypes.includes("macroEnabled");
    const ext = isXlsm ? "xlsm" : "xlsx";
    const mime = isXlsm
      ? "application/vnd.ms-excel.sheet.macroEnabled.12"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const bytes = await zip.generateAsync({
      type: "uint8array",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });
    const filename = `${(report.reportTitle || "Proctor_Report").replace(/\s+/g, "_")}.${ext}`;
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Excel export failed:", err);
    alert("Excel export failed: " + err.message);
  }
}

// ─── PDF Export ────────────────────────────────────────────────────────────────
export function exportProctorToPDF(report, template, chartImgDataURL, returnBlob = false) {
  const trials = computeTrials(report, template);
  const validTrials = trials.filter(Boolean);

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const mL = 36, mR = 36, mT = 36;
  const contentW = pageW - mL - mR;
  let y = mT;

  const addrMapPDF = {};
  if (template) {
    for (const sec of template.sections || []) {
      for (const field of sec.fields || []) {
        if (field._addr && report[field.id] !== undefined && report[field.id] !== "") {
          addrMapPDF[field._addr.toUpperCase()] = String(report[field.id]);
        }
      }
    }
  }
  const FIELD_ADDR_PDF = { project:"B3",county:"E3",permit:"B4",method:"E4",location:"B5",lab:"E5",material:"B6",testedby:"E6",testdate:"B7",specitem:"E7",ov_pct:"O4",ov_gm:"O5",ov_yw:"O6" };
  const v = (id) => {
    const byId = report[id];
    if (byId !== undefined && byId !== null && byId !== "") return String(byId);
    const addr = FIELD_ADDR_PDF[id];
    if (addr && addrMapPDF[addr]) return addrMapPDF[addr];
    return "";
  };

  const checkPage = (need=20) => { if (y + need > pageH - 36) { doc.addPage(); y = mT; } };

  // ── Cell helper ────────────────────────────────────────────────────────────
  const cell = (x, cy, w, h, txt, opts = {}) => {
    const { bg=[255,255,255], bold=false, fontSize=8, align="left", textColor=[20,20,20], italic=false, border=true } = opts;
    doc.setFillColor(...bg); doc.rect(x, cy, w, h, "F");
    if (border) { doc.setDrawColor(160,160,160); doc.setLineWidth(0.25); doc.rect(x, cy, w, h, "S"); }
    if (!txt && txt !== 0) return;
    const str = String(txt);
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", italic ? "italic" : bold ? "bold" : "normal");
    doc.setTextColor(...textColor);
    const pad = 3;
    const lines = doc.splitTextToSize(str, w - pad*2);
    const lh = fontSize * 1.25;
    let ty = cy + (h - lines.length * lh) / 2 + fontSize * 0.72;
    lines.forEach(line => {
      if (align==="center") doc.text(line, x + w/2, ty, {align:"center"});
      else doc.text(line, x + pad, ty);
      ty += lh;
    });
  };

  const secBar = (label, fy, fw=contentW) => {
    doc.setFillColor(55,55,55); doc.rect(mL, fy, fw, 14, "F");
    doc.setFontSize(9); doc.setFont("helvetica","bold"); doc.setTextColor(255,255,255);
    doc.text(label, mL + fw/2, fy + 9.5, {align:"center"});
    return fy + 14;
  };

  // ── TITLE ──────────────────────────────────────────────────────────────────
  const titleText = report.reportTitle || "D698/D1557 — Moisture–Density Relationship";
  doc.setFillColor(40,40,40); doc.rect(mL, y, contentW, 20, "F");
  doc.setFontSize(11); doc.setFont("helvetica","bold"); doc.setTextColor(255,255,255);
  doc.text(titleText, mL+6, y+13);
  y += 22;

  // ── HEADER + OVERSIZE side by side ────────────────────────────────────────
  const headerStartY = y;
  const leftW = contentW * 0.55;
  const rightW = contentW - leftW - 4;
  const rightX = mL + leftW + 4;
  const rH = 16;

  const headerPairs = [
    [{id:"project",label:"Project"}, {id:"county",label:"County"}],
    [{id:"permit",label:"Permit"},   {id:"method",label:"Proctor Method"}],
    [{id:"location",label:"Location"},{id:"lab",label:"Lab"}],
    [{id:"material",label:"Material / Source"},{id:"testedby",label:"Tested By"}],
    [{id:"testdate",label:"Test Date"},{id:"specitem",label:"Spec / Item"}],
  ];
  const lw=leftW*0.30, vw=leftW*0.20;
  headerPairs.forEach(([f1,f2]) => {
    cell(mL,        y, lw, rH, f1.label+":", {bold:true, bg:[240,240,240], fontSize:7.5});
    cell(mL+lw,     y, vw, rH, v(f1.id),    {fontSize:8});
    cell(mL+lw+vw,  y, lw, rH, f2.label+":", {bold:true, bg:[240,240,240], fontSize:7.5});
    cell(mL+lw+vw+lw, y, vw, rH, v(f2.id), {fontSize:8});
    y += rH;
  });

  // Oversize table (right side, drawn alongside header)
  let oy = headerStartY;
  const ovH = 13;
  doc.setFillColor(185,185,185); doc.rect(rightX, oy, rightW, ovH, "F");
  doc.setDrawColor(160,160,160); doc.setLineWidth(0.25); doc.rect(rightX, oy, rightW, ovH, "S");
  doc.setFontSize(7.5); doc.setFont("helvetica","bold"); doc.setTextColor(20,20,20);
  doc.text("OVERSIZE CORRECTION (ASTM D4718)", rightX+rightW/2, oy+8.5, {align:"center"});
  oy += ovH;

  const iW=rightW*0.44, vW2=rightW*0.16, nW=rightW*0.40;
  cell(rightX,        oy, iW,  ovH, "Input", {bold:true, bg:[210,210,210], align:"center", fontSize:7.5});
  cell(rightX+iW,     oy, vW2, ovH, "Value", {bold:true, bg:[210,210,210], align:"center", fontSize:7.5});
  cell(rightX+iW+vW2, oy, nW,  ovH, "Notes", {bold:true, bg:[210,210,210], align:"center", fontSize:7.5});
  oy += ovH;

  [
    {id:"ov_pct", label:"Percent oversize P (%) (>3/4 in, by wt)", note:"Enter from gradation; sum % retained on 3/4 in. sieve"},
    {id:"ov_gm",  label:"Bulk specific gravity of oversize, Gm",   note:"Typical crushed stone"},
    {id:"ov_yw",  label:"Unit weight of water γw (pcf)",            note:"Constant"},
  ].forEach(f => {
    const rowH = 18;
    cell(rightX,        oy, iW,  rowH, f.label, {fontSize:6.5, bg:[250,250,250]});
    cell(rightX+iW,     oy, vW2, rowH, v(f.id) || PROCTOR_FIELDS.find(pf=>pf.id===f.id)?.placeholder || "", {fontSize:8, align:"center"});
    cell(rightX+iW+vW2, oy, nW,  rowH, f.note,  {fontSize:6.5, bg:[250,250,250], textColor:[90,90,90], italic:true});
    oy += rowH;
  });

  y = Math.max(y, oy) + 6;

  // ── COMPACTION TEST DATA ────────────────────────────────────────────────────
  checkPage(80);
  y = secBar("COMPACTION TEST DATA", y);

  const inputCols = [
    {key:"moldwet", label:"Mold + Wet\n(g)"},
    {key:"mold",    label:"Mold\n(g)"},
    {key:"moldvol", label:"Mold Vol\n(ft³)"},
    {key:"tarewet", label:"Tare + Wet\n(g)"},
    {key:"taredry", label:"Tare + Dry\n(g)"},
    {key:"tare",    label:"Tare Wt\n(g)"},
  ];
  const calcCols = [
    {key:"wetDensity", label:"Wet Density\n(pcf)*"},
    {key:"moisture",   label:"Moisture\n(%)*"},
    {key:"dryDensity", label:"Dry Density\n(pcf)*"},
    {key:"ddCorr",     label:"DD CORR\n(pcf)*"},
  ];
  const allCols = [...inputCols, ...calcCols];
  const trialW = 28;
  const dataW = (contentW - trialW) / allCols.length;
  const hdrH = 26;

  // Header
  cell(mL, y, trialW, hdrH, "Trial", {bold:true, bg:[210,210,210], align:"center", fontSize:7});
  allCols.forEach((col, i) => {
    const isCalc = calcCols.includes(col);
    cell(mL + trialW + i*dataW, y, dataW, hdrH, col.label, {bold:true, bg: isCalc?[195,195,195]:[210,210,210], align:"center", fontSize:6.5});
  });
  y += hdrH;

  // Rows
  for (let t=1; t<=4; t++) {
    checkPage(14);
    const tr = trials[t-1];
    const bg = t%2===0 ? [248,248,248] : [255,255,255];
    const calcBg = t%2===0 ? [235,235,235] : [242,242,242];
    cell(mL, y, trialW, 14, String(t), {align:"center", fontSize:8, bg});
    inputCols.forEach((col, i) => {
      let val = v(`t${t}_${col.key}`);
      if (!val) {
        const addr = TRIAL_ADDR[t - 1]?.[col.key];
        if (addr) val = addrMapPDF[addr.toUpperCase()] || "";
      }
      cell(mL + trialW + i*dataW, y, dataW, 14, val, {align:"center", fontSize:8, bg});
    });
    calcCols.forEach((col, i) => {
      const val = tr ? tr[col.key]?.toFixed(2) : "";
      cell(mL + trialW + (inputCols.length+i)*dataW, y, dataW, 14, val||"", {align:"center", fontSize:8, bg:calcBg, textColor:tr?[20,20,20]:[160,160,160]});
    });
    y += 14;
  }
  doc.setFontSize(7); doc.setFont("helvetica","italic"); doc.setTextColor(120,120,120);
  doc.text("* Calculated from inputs — open in Excel for full formula results", mL, y+9);
  y += 16;

  // ── RESULTS ────────────────────────────────────────────────────────────────
  checkPage(50);
  y = secBar("RESULTS", y);

  // Compute OMC/MDD from quadratic fit
  let omc_u="(calculated)", mdd_u="(calculated)", omc_c="(calculated)", mdd_c="(calculated)";
  if (validTrials.length >= 3) {
    const ptU = validTrials.map(tr => ({x: tr.moisture, y: tr.dryDensity}));
    const ptC = validTrials.map(tr => ({x: tr.moisture, y: tr.ddCorr}));
    const coeffU = fitQuadratic(ptU);
    const coeffC = fitQuadratic(ptC);
    if (coeffU && coeffU.a < 0) {
      const xPeak = -coeffU.b / (2*coeffU.a);
      const yPeak = coeffU.a*xPeak*xPeak + coeffU.b*xPeak + coeffU.c;
      omc_u = xPeak.toFixed(2) + " %";
      mdd_u = yPeak.toFixed(2) + " pcf";
    }
    if (coeffC && coeffC.a < 0) {
      const xPeak = -coeffC.b / (2*coeffC.a);
      const yPeak = coeffC.a*xPeak*xPeak + coeffC.b*xPeak + coeffC.c;
      omc_c = xPeak.toFixed(2) + " %";
      mdd_c = yPeak.toFixed(2) + " pcf";
    }
  }

  const resH = 17;
  const hw = contentW/2, lw2 = hw*0.55, vw3 = hw*0.45;
  const resRows = [
    [{label:"Uncorrected MDD (pcf)", val:mdd_u}, {label:"Uncorrected OMC (%)", val:omc_u}],
    [{label:"Corrected MDD (pcf)",   val:mdd_c}, {label:"Corrected OMC (%)",   val:omc_c}],
  ];
  resRows.forEach(([r1, r2]) => {
    cell(mL,        y, lw2, resH, r1.label, {bold:true, bg:[240,240,240], fontSize:7.5});
    cell(mL+lw2,    y, vw3, resH, r1.val,   {fontSize:8, align:"center", bg:r1.val.includes("calc")?[218,218,218]:[255,255,255], italic:r1.val.includes("calc")});
    cell(mL+hw,     y, lw2, resH, r2.label, {bold:true, bg:[240,240,240], fontSize:7.5});
    cell(mL+hw+lw2, y, vw3, resH, r2.val,   {fontSize:8, align:"center", bg:r2.val.includes("calc")?[218,218,218]:[255,255,255], italic:r2.val.includes("calc")});
    y += resH;
  });
  y += 6;

  // ── PROCTOR CURVE CHART ────────────────────────────────────────────────────
  if (chartImgDataURL) {
    // Embed the Chart.js canvas PNG directly — crisp, accurate, matches what's on screen
    const chartH = 310;
    checkPage(chartH + 10);
    // Aspect ratio of the Chart.js canvas is roughly 2:1
    const chartW = contentW;
    doc.addImage(chartImgDataURL, "PNG", mL, y, chartW, chartH);
    y += chartH + 8;
  } else if (validTrials.length >= 2) {
    // Fallback: simple placeholder box if chart ref wasn't available
    checkPage(40);
    doc.setFillColor(245,245,245); doc.setDrawColor(200,200,200); doc.setLineWidth(0.3);
    doc.rect(mL, y, contentW, 30, "FD");
    doc.setFontSize(9); doc.setFont("helvetica","italic"); doc.setTextColor(150,150,150);
    doc.text("Proctor curve chart — open report in app and export from there to include chart", mL + contentW/2, y+18, {align:"center"});
    y += 38;
  }

  // ── FOOTER ─────────────────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  for (let p=1; p<=pages; p++) {
    doc.setPage(p);
    doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(150,150,150);
    doc.setDrawColor(190,190,190); doc.setLineWidth(0.3);
    doc.line(mL, pageH-22, pageW-mR, pageH-22);
    doc.text(titleText, mL, pageH-13);
    doc.text(`Page ${p} of ${pages}`, pageW-mR, pageH-13, {align:"right"});
  }

  if (returnBlob) return doc.output("blob");
  doc.save(`${titleText.replace(/[^a-zA-Z0-9]/g,"_")}.pdf`);
}

// ─── Proctor Preview Component ─────────────────────────────────────────────────
export function ProctorPreview({ report, template, rawBytes }) {
  // Build addr→user-value map from template sections
  const addrMap = React.useMemo(() => {
    const map = {};
    if (!template) return map;
    for (const sec of template.sections || []) {
      for (const field of sec.fields || []) {
        if (field._addr && report[field.id] !== undefined && report[field.id] !== "") {
          map[field._addr.toUpperCase()] = String(report[field.id]);
        }
      }
    }
    return map;
  }, [template, report]); // eslint-disable-line

  // Evaluate ALL cells (input + formula) using Excel's own formulas with current values.
  // Memoised so it only re-runs when something actually changes, not on every render.
  const evalValues = React.useMemo(
    () => (rawBytes && template) ? evalFormulasWithReport(template, rawBytes, report) : {},
    [rawBytes, template, report], // eslint-disable-line
  );

  // Debug: shows all numeric cells so we can verify cell addresses if needed
  React.useEffect(() => {
    if (!rawBytes || !Object.keys(evalValues).length) return;
    const nums = Object.fromEntries(
      Object.entries(evalValues).filter(([, v]) => !isNaN(parseFloat(v)))
    );
    console.log("[ProctorPreview] evalValues (numeric cells):", nums);
  }, [evalValues]); // eslint-disable-line


  // Field ID → value lookup (falls back to cell address)
  const FIELD_ADDR = { project:"B3",county:"E3",permit:"B4",method:"E4",location:"B5",lab:"E5",material:"B6",testedby:"E6",testdate:"B7",specitem:"E7", ov_pct:"O4",ov_gm:"O5",ov_yw:"O6" };
  const v = (id) => {
    const byId = report[id];
    if (byId !== undefined && byId !== null && byId !== "") return String(byId);
    const addr = FIELD_ADDR[id];
    if (addr && addrMap[addr]) return addrMap[addr];
    return "";
  };

  // Build trial rows directly from evalValues using extended TRIAL_ADDR.
  // A trial is considered present only if the user entered a moldwet value.
  const trialRows = TRIAL_ADDR.map((addrs, i) => {
    if (!addrMap[addrs.moldwet.toUpperCase()]) return null;
    const get = (key) => {
      const addr = addrs[key]; if (!addr) return NaN;
      const val = evalValues[addr.toUpperCase()]; return val !== undefined ? parseFloat(val) : NaN;
    };
    return {
      t: i + 1,
      moldwet:    get("moldwet"),
      mold:       get("mold"),
      wetsoil:    get("wetsoil"),
      moldvol:    get("moldvol"),
      tarewet:    get("tarewet"),
      taredry:    get("taredry"),
      tare:       get("tare"),
      wetDensity: get("wetdensity"),
      moisture:   get("moisture"),
      dryDensity: get("dryDensity"),
      ddCorr:     get("ddCorr"),
    };
  });
  // Only count a trial as valid if moisture and dryDensity computed successfully
  const validTrials = trialRows.filter(t => t && !isNaN(t.moisture) && !isNaN(t.dryDensity));

  // Read MDD/OMC directly from evaluated Excel cells — LET/LINEST/INDEX now supported.
  const fmtNum = (addr, unit) => {
    const n = parseFloat(evalValues[addr]);
    return (!isNaN(n) && n) ? n.toFixed(2) + " " + unit : "—";
  };
  const mdd_u = fmtNum("B15", "pcf");
  const omc_u = fmtNum("E15", "%");
  const mdd_c = fmtNum("B16", "pcf");
  const omc_c = fmtNum("E16", "%");
  // Single unified column list — calc:true columns come from Excel formula evaluation
  const allCols = [
    { key: "moldwet",    label: "Mold + Wet\n(g)",       calc: false },
    { key: "mold",       label: "Mold (g)",               calc: false },
    { key: "wetsoil",    label: "Wet Soil\n(g)",          calc: true  },
    { key: "moldvol",    label: "Mold Vol\n(ft³)",        calc: false },
    { key: "tarewet",    label: "Tare + Wet\n(g)",        calc: false },
    { key: "taredry",    label: "Tare + Dry\n(g)",        calc: false },
    { key: "tare",       label: "Tare Wt\n(g)",           calc: false },
    { key: "wetDensity", label: "Wet Density\n(pcf)",     calc: true  },
    { key: "moisture",   label: "Moisture\n(%)",          calc: true  },
    { key: "dryDensity", label: "Dry Density\n(pcf)",     calc: true  },
    { key: "ddCorr",     label: "DD Corr\n(pcf)",         calc: true  },
  ];

  const S = {
    paper: { background: "#fff", maxWidth: 900, margin: "0 auto", fontFamily: "Arial, sans-serif", fontSize: 12, color: "#111", boxShadow: "0 2px 16px rgba(0,0,0,0.13)", borderRadius: 4, overflow: "hidden" },
    titleBar: { background: "#282828", color: "#fff", fontWeight: 700, fontSize: 13, padding: "8px 14px" },
    secBar: { background: "#373737", color: "#fff", fontWeight: 700, fontSize: 11, textAlign: "center", padding: "5px 0", letterSpacing: 0.5 },
    labelCell: { background: "#f0f0f0", fontWeight: 700, fontSize: 11, padding: "4px 6px", border: "1px solid #bbb" },
    valCell: { background: "#fff", fontSize: 11, padding: "4px 6px", border: "1px solid #bbb" },
    thCell: { background: "#d2d2d2", fontWeight: 700, fontSize: 10, textAlign: "center", padding: "4px 3px", border: "1px solid #bbb", whiteSpace: "pre-line" },
    thCalc: { background: "#c0c0c0", fontWeight: 700, fontSize: 10, textAlign: "center", padding: "4px 3px", border: "1px solid #bbb", whiteSpace: "pre-line" },
    tdCell: (even) => ({ background: even ? "#f8f8f8" : "#fff", fontSize: 11, textAlign: "center", padding: "3px 4px", border: "1px solid #bbb" }),
    tdCalc: (even) => ({ background: even ? "#ebebeb" : "#f2f2f2", fontSize: 11, textAlign: "center", padding: "3px 4px", border: "1px solid #bbb" }),
    calcHint: { fontSize: 10, fontStyle: "italic", color: "#888", padding: "4px 8px" },
  };

  return (
    <div style={S.paper}>
      {/* Title */}
      <div style={S.titleBar}>{report.reportTitle || "D698/D1557 — Moisture–Density Relationship"}</div>

      {/* Header + Oversize side by side */}
      <div style={{ display: "flex", gap: 0, padding: "10px 12px 6px" }}>
        {/* Left: General info */}
        <div style={{ flex: "0 0 55%", paddingRight: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {[
                [{ id: "project", label: "Project" }, { id: "county", label: "County" }],
                [{ id: "permit",  label: "Permit" },  { id: "method", label: "Proctor Method" }],
                [{ id: "location",label: "Location" },{ id: "lab",    label: "Lab" }],
                [{ id: "material",label: "Material / Source" },{ id: "testedby", label: "Tested By" }],
                [{ id: "testdate",label: "Test Date" },{ id: "specitem", label: "Spec / Item" }],
              ].map(([f1, f2], i) => (
                <tr key={i}>
                  <td style={{ ...S.labelCell, width: "22%" }}>{f1.label}:</td>
                  <td style={{ ...S.valCell, width: "28%" }}>{v(f1.id)}</td>
                  <td style={{ ...S.labelCell, width: "22%" }}>{f2.label}:</td>
                  <td style={{ ...S.valCell, width: "28%" }}>{v(f2.id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Right: Oversize correction */}
        <div style={{ flex: 1 }}>
          <div style={{ background: "#b9b9b9", fontWeight: 700, fontSize: 10, textAlign: "center", padding: "4px 0", border: "1px solid #bbb" }}>OVERSIZE CORRECTION (ASTM D4718)</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...S.thCell, width: "44%" }}>Input</th>
                <th style={{ ...S.thCell, width: "16%" }}>Value</th>
                <th style={{ ...S.thCell, width: "40%" }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {[
                { id: "ov_pct", label: "Percent oversize P (%) (>3/4 in, by wt)", note: "Enter from gradation; sum % retained on 3/4 in. sieve" },
                { id: "ov_gm",  label: "Bulk specific gravity of oversize, Gm",    note: "Typical crushed stone" },
                { id: "ov_yw",  label: "Unit weight of water γw (pcf)",             note: "Constant" },
              ].map(f => (
                <tr key={f.id}>
                  <td style={{ ...S.valCell, fontSize: 10 }}>{f.label}</td>
                  <td style={{ ...S.valCell, textAlign: "center", fontSize: 11 }}>{v(f.id) || PROCTOR_FIELDS.find(pf => pf.id === f.id)?.placeholder || ""}</td>
                  <td style={{ ...S.valCell, fontSize: 9, color: "#666", fontStyle: "italic" }}>{f.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Compaction Test Data */}
      <div style={{ padding: "0 12px 6px" }}>
        <div style={S.secBar}>COMPACTION TEST DATA</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...S.thCell, width: 36 }}>Trial</th>
              {allCols.map(c => <th key={c.key} style={c.calc ? S.thCalc : S.thCell}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3, 4].map(t => {
              const tr = trialRows[t - 1];
              const even = t % 2 === 0;
              return (
                <tr key={t}>
                  <td style={S.tdCell(even)}>{t}</td>
                  {allCols.map(c => {
                    const val = tr ? (isNaN(tr[c.key]) ? "" : Number(tr[c.key]).toFixed(2)) : "";
                    return <td key={c.key} style={c.calc ? S.tdCalc(even) : S.tdCell(even)}>{val}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Results */}
      <div style={{ padding: "0 12px 6px" }}>
        <div style={S.secBar}>RESULTS</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {[
              [{ label: "Uncorrected MDD (pcf)", val: mdd_u }, { label: "Uncorrected OMC (%)", val: omc_u }],
              [{ label: "Corrected MDD (pcf)",   val: mdd_c }, { label: "Corrected OMC (%)",   val: omc_c }],
            ].map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <React.Fragment key={j}>
                    <td style={{ ...S.labelCell, width: "30%" }}>{cell.label}</td>
                    <td style={{ ...S.valCell, textAlign: "center", width: "20%", fontStyle: cell.val === "—" ? "italic" : "normal", color: cell.val === "—" ? "#999" : "#111" }}>{cell.val}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Proctor Curve Chart */}
      <div style={{ padding: "6px 12px 14px" }}>
        <div style={S.secBar}>PROCTOR COMPACTION CURVE</div>
        <div style={{ marginTop: 4 }}>
          {validTrials.length >= 2
            ? <ProctorChart trials={trialRows} />
            : <div style={{ textAlign: "center", padding: "28px 0", color: "#999", fontStyle: "italic", fontSize: 12 }}>Enter at least 2 trial data points to see the curve</div>
          }
        </div>
      </div>
    </div>
  );
}
